import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import './DemoRequestDetailPage.css';

const STATUS_OPTIONS = [
  'New', 'Under Review', 'Qualified', 'Contacted',
  'Meeting Scheduled', 'Not a Fit', 'Closed',
];

const PRIORITY_OPTIONS = ['Low', 'Normal', 'High', 'Urgent'];

const STATUS_COLORS = {
  'New': '#3B82F6',
  'Under Review': '#F59E0B',
  'Qualified': '#10B981',
  'Contacted': '#8B5CF6',
  'Meeting Scheduled': '#06B6D4',
  'Not a Fit': '#6B7280',
  'Closed': '#374151',
};

export default function DemoRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editNextStep, setEditNextStep] = useState('');
  const [newNote, setNewNote] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiService.getDemoRequest(id);
      setData(result);
      setEditStatus(result.status || 'New');
      setEditPriority(result.priority || 'Normal');
      setEditAssignedTo(result.assigned_to || '');
      setEditNextStep(result.next_step || '');
    } catch (err) {
      setError('Failed to load demo request.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const update = {
        status: editStatus,
        priority: editPriority,
        assigned_to: editAssignedTo || null,
        next_step: editNextStep || null,
      };
      if (newNote.trim()) {
        const existing = data.internal_notes || '';
        const timestamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
        const combined = existing
          ? `${existing}\n\n[${timestamp}]\n${newNote.trim()}`
          : `[${timestamp}]\n${newNote.trim()}`;
        update.internal_notes = combined;
      }
      await apiService.updateDemoRequest(id, update);
      setSuccess('Updated successfully.');
      setNewNote('');
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) return <div className="drd"><div className="drd-loading">Loading...</div></div>;
  if (!data) return <div className="drd"><div className="drd-error">{error || 'Not found.'}</div></div>;

  const interest = Array.isArray(data.interest_type) ? data.interest_type : [];

  return (
    <div className="drd">
      <div className="drd-topbar">
        <button className="drd-btn--back" onClick={() => navigate('/system/demo-requests')}>
          &larr; Back to List
        </button>
        <div className="drd-topbar__right">
          <span className="drd-status-badge" style={{ borderColor: STATUS_COLORS[data.status], color: STATUS_COLORS[data.status] }}>
            {data.status}
          </span>
          {data.is_flagged && <span className="drd-flag-badge">FLAGGED</span>}
          {data.is_duplicate && <span className="drd-dup-badge">POSSIBLE DUPLICATE</span>}
        </div>
      </div>

      {error && <div className="drd-alert drd-alert--error">{error}</div>}
      {success && <div className="drd-alert drd-alert--success">{success}</div>}

      <div className="drd-layout">
        {/* ── Left: Submission Data ── */}
        <div className="drd-main">
          <h1 className="drd-title">{data.first_name} {data.last_name}</h1>
          <p className="drd-subtitle">{data.job_title} at {data.company_name}</p>

          {/* Contact */}
          <section className="drd-section">
            <h3 className="drd-section__title">Contact Information</h3>
            <div className="drd-info-grid">
              <div className="drd-info"><span className="drd-info__label">Email</span><span>{data.work_email}</span></div>
              <div className="drd-info"><span className="drd-info__label">Email Domain</span><span>{data.email_domain}</span></div>
              <div className="drd-info"><span className="drd-info__label">Phone (Raw)</span><span>{data.phone_raw}</span></div>
              <div className="drd-info"><span className="drd-info__label">Phone (Normalized)</span><span>{data.phone_normalized || '—'}</span></div>
            </div>
          </section>

          {/* Company */}
          <section className="drd-section">
            <h3 className="drd-section__title">Company Details</h3>
            <div className="drd-info-grid">
              <div className="drd-info"><span className="drd-info__label">Company</span><span>{data.company_name}</span></div>
              <div className="drd-info"><span className="drd-info__label">Website</span>
                <span>{data.company_website ? <a href={data.company_website} target="_blank" rel="noopener noreferrer">{data.company_website}</a> : '—'}</span>
              </div>
              <div className="drd-info"><span className="drd-info__label">Size</span><span>{data.company_size}</span></div>
              <div className="drd-info"><span className="drd-info__label">Industry</span><span>{data.industry}</span></div>
            </div>
          </section>

          {/* Business Need */}
          <section className="drd-section">
            <h3 className="drd-section__title">Business Need</h3>
            <div className="drd-usecase">
              <h4 className="drd-usecase__label">Primary Use Case <span className="drd-wc">({data.primary_use_case_word_count} words)</span></h4>
              <p className="drd-usecase__text">{data.primary_use_case}</p>
            </div>
            {data.current_state_notes && (
              <div className="drd-usecase" style={{ marginTop: '1rem' }}>
                <h4 className="drd-usecase__label">Current State / Existing Efforts</h4>
                <p className="drd-usecase__text">{data.current_state_notes}</p>
              </div>
            )}
            <div className="drd-info-grid" style={{ marginTop: '1rem' }}>
              <div className="drd-info"><span className="drd-info__label">Timeline</span><span>{data.timeline}</span></div>
              <div className="drd-info"><span className="drd-info__label">Interest</span>
                <span className="drd-detail-tags">{interest.map(i => <span key={i} className="drd-tag">{i}</span>)}</span>
              </div>
            </div>
          </section>

          {/* Qualification */}
          {(data.team_involved || data.expected_scale) && (
            <section className="drd-section">
              <h3 className="drd-section__title">Qualification</h3>
              <div className="drd-info-grid">
                <div className="drd-info"><span className="drd-info__label">Team Involved</span><span>{data.team_involved || '—'}</span></div>
                <div className="drd-info"><span className="drd-info__label">Expected Scale</span><span>{data.expected_scale || '—'}</span></div>
              </div>
            </section>
          )}

          {/* Metadata */}
          <section className="drd-section drd-section--meta">
            <h3 className="drd-section__title">Metadata</h3>
            <div className="drd-info-grid drd-info-grid--3">
              <div className="drd-info"><span className="drd-info__label">Submitted</span><span>{formatDate(data.created_at)}</span></div>
              <div className="drd-info"><span className="drd-info__label">Last Viewed</span><span>{formatDate(data.last_viewed_at)}</span></div>
              <div className="drd-info"><span className="drd-info__label">Consent</span><span>{data.consent_given ? 'Yes' : 'No'}</span></div>
              <div className="drd-info"><span className="drd-info__label">IP Address</span><span>{data.ip_address || '—'}</span></div>
              <div className="drd-info"><span className="drd-info__label">Source Page</span><span>{data.source_page || '—'}</span></div>
              <div className="drd-info"><span className="drd-info__label">Referrer</span><span>{data.referrer_url || '—'}</span></div>
              <div className="drd-info"><span className="drd-info__label">UTM Source</span><span>{data.utm_source || '—'}</span></div>
              <div className="drd-info"><span className="drd-info__label">UTM Medium</span><span>{data.utm_medium || '—'}</span></div>
              <div className="drd-info"><span className="drd-info__label">UTM Campaign</span><span>{data.utm_campaign || '—'}</span></div>
              <div className="drd-info"><span className="drd-info__label">User Agent</span><span className="drd-info--truncate">{data.user_agent || '—'}</span></div>
            </div>
          </section>
        </div>

        {/* ── Right: Admin Panel ── */}
        <div className="drd-sidebar">
          <div className="drd-admin-card">
            <h3 className="drd-admin-card__title">Admin Actions</h3>

            <div className="drd-admin-field">
              <label>Status</label>
              <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="drd-admin-field">
              <label>Priority</label>
              <select value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div className="drd-admin-field">
              <label>Assigned To</label>
              <input type="text" value={editAssignedTo} onChange={(e) => setEditAssignedTo(e.target.value)} placeholder="Owner name or email" />
            </div>

            <div className="drd-admin-field">
              <label>Next Step</label>
              <input type="text" value={editNextStep} onChange={(e) => setEditNextStep(e.target.value)} placeholder="Next action to take" />
            </div>

            <div className="drd-admin-field">
              <label>Add Note</label>
              <textarea rows={3} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Write an internal note..." />
            </div>

            <button className="drd-btn--save" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>

            {data.last_contacted_at && (
              <p className="drd-admin-meta">Last contacted: {formatDate(data.last_contacted_at)}</p>
            )}
          </div>

          {/* Notes History */}
          {data.internal_notes && (
            <div className="drd-admin-card">
              <h3 className="drd-admin-card__title">Internal Notes</h3>
              <pre className="drd-notes-history">{data.internal_notes}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
